<template>
  <div class="post-media-selection-panel">
    <div v-if="props.showAlbumPickerLink" class="post-media-selection-panel__actions">
      <LinkWithIcon isDottedUnderline @click="emit('openAlbumPicker')">
        <template #icon><IconEmbedded name="folder_2" :size="19" /></template>
        <span>{{ props.albumPickerLabel }}</span>
      </LinkWithIcon>
      <span class="post-media-selection-panel__actions-separator"> </span>
      <LinkWithIcon isDottedUnderline @click="isUploadSectionOpen = !isUploadSectionOpen">
        <template #icon><IconEmbedded name="upload_2" :size="17" /></template>
        <span>{{ uploadMediaLabel }}</span>
      </LinkWithIcon>
    </div>

    <p v-if="showNoVideosMessage" class="post-media-selection-panel__no-videos-message">Album has no videos</p>

    <div v-if="selectionValidationError" class="global-error-string" style="margin-bottom: 8px">{{ selectionValidationError }}</div>

    <PhotoUploadSection
      v-if="isUploadSectionOpen"
      class="post-media-selection-panel__upload-section"
      :inputId="props.inputId"
      v-model="uploadedAssetIds"
      :selectedAssetIds="selectedAssetIds"
      :ownerId="props.customerId"
      :maxPhotos="maxMediaCount"
      :isVideoUploadAllowed="isVideoUploadAllowed"
      :isVideoOnlyUploadAllowed="isVideoOnlyUploadAllowed"
      :beforeFileUploadValidate="beforeFileUploadValidate"
      @select="handleUploadSelect"
      @deselect="handleUploadDeselect"
      @close="isUploadSectionOpen = false"
    />

    <ul v-if="props.isLoadingAlbum" class="post-media-selection-panel__thumbnails-list post-media-selection-panel__thumbnails-list--skeleton">
      <li v-for="i in 12" :key="i" class="post-media-selection-panel__thumbnail post-media-selection-panel__thumbnail--skeleton"></li>
    </ul>

    <ul v-else-if="props.showThumbnailGrid" class="post-media-selection-panel__thumbnails-list">
      <SrpFileThumbnail
        class="post-media-selection-panel__thumbnail"
        v-for="assetId in props.displayedAssetIds"
        :key="assetId"
        :fileName="assetId"
        :lightboxCaption="selectedAssetIds.includes(assetId) ? 'Image is selected' : null"
        :galleryPostfix="props.galleryPostfix"
        :isDeleteButton="false"
        isEntireAreaCheckable
      >
        <input
          type="checkbox"
          :value="assetId"
          :checked="selectedAssetIds.includes(assetId)"
          :disabled="isAtLimit && maxMediaCount > 1 && !selectedAssetIds.includes(assetId)"
          @change="$event => handleCheckboxChange(($event.target as HTMLInputElement).checked, assetId)"
        />
      </SrpFileThumbnail>
    </ul>

    <div class="post-media-selection-panel__sticky-panel">
      <PhotoLimitWarning :isAtLimit="isAtLimit" :maxPhotos="maxMediaCount" />

      <SelectedFilesPanel
        v-if="selectedAssetIds.length && maxMediaCount > 1"
        class="post-media-selection-panel__selected-files-panel"
        :selectedFiles="selectedAssetIds.map(id => ({ serverId: id })) as UploadedImage[]"
        @remove="assetId => handleCheckboxChange(false, assetId)"
        @reorder="newArray => (selectedAssetIds = newArray.map(({ serverId }) => serverId))"
        :isPreviewLinkVisible="false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from "vue";

import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PhotoUploadSection from "./PhotoUploadSection.vue";
import PhotoLimitWarning from "./PhotoLimitWarning.vue";

import { UploadedImage } from "@contracts/uploadedImage";
import { type AlbumMediaPostType, selectOrDeselectMediaAsset, showNoVideosInAlbumMessage, validateAlbumAssetForPostType } from "@logic/AtlasSchedulingValidation";
import { usePostTypeMediaRules } from "@composables/usePostTypeMediaRules";

const props = withDefaults(
  defineProps<{
    customerId: string;
    postType: AlbumMediaPostType;
    displayedAssetIds: string[];
    inputId: string;
    galleryPostfix: string;
    isLoadingAlbum?: boolean;
    showThumbnailGrid?: boolean;
    showAlbumPickerLink?: boolean;
    albumPickerLabel?: string;
    hasAlbum?: boolean;
  }>(),
  {
    isLoadingAlbum: false,
    showThumbnailGrid: true,
    showAlbumPickerLink: true,
    albumPickerLabel: "Select from an Album",
    hasAlbum: false,
  }
);

const selectedAssetIds = defineModel<string[]>("selectedAssetIds", { required: true });
const uploadedAssetIds = defineModel<string[]>("uploadedAssetIds", { default: () => [] });

const emit = defineEmits<{
  (e: "openAlbumPicker"): void;
  (e: "selectionChanged"): void;
  (e: "uploadSelected", assetId: string): void;
  (e: "uploadDeselected", assetId: string): void;
}>();

const isUploadSectionOpen = ref(false);
const selectionValidationError = ref("");

const {
  maxMediaCount,
  isVideoUploadAllowed,
  isVideoOnlyUploadAllowed,
  beforeFileUploadValidate,
  uploadMediaLabel,
  isReelPost,
} = usePostTypeMediaRules(toRef(props, "postType"));

const isAtLimit = computed(() => selectedAssetIds.value.length >= maxMediaCount.value);

const showNoVideosMessage = computed(() =>
  showNoVideosInAlbumMessage(props.postType, props.hasAlbum, props.isLoadingAlbum, props.displayedAssetIds.length)
);

function applySelection(isSelected: boolean, assetId: string) {
  const previousIds = selectedAssetIds.value;
  const nextIds = selectOrDeselectMediaAsset(previousIds, assetId, isSelected, maxMediaCount.value);
  if (nextIds === previousIds) {
    return false;
  }
  selectedAssetIds.value = nextIds;
  emit("selectionChanged");
  return true;
}

async function tryApplySelection(isSelected: boolean, assetId: string): Promise<boolean> {
  if (!isSelected) {
    selectionValidationError.value = "";
    return applySelection(false, assetId);
  }

  if (isReelPost.value) {
    const result = await validateAlbumAssetForPostType(props.postType, assetId);
    if (!result.ok) {
      selectionValidationError.value = result.message;
      return false;
    }
  }

  selectionValidationError.value = "";
  return applySelection(true, assetId);
}

async function handleCheckboxChange(isSelected: boolean, assetId: string) {
  await tryApplySelection(isSelected, assetId);
}

async function handleUploadSelect(assetId: string) {
  if (await tryApplySelection(true, assetId)) {
    emit("uploadSelected", assetId);
  }
}

function handleUploadDeselect(assetId: string) {
  if (applySelection(false, assetId)) {
    emit("uploadDeselected", assetId);
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";
@import "./photo-upload";

.post-media-selection-panel {
  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }

  &__actions-separator {
    color: rgba(0, 0, 0, 0.5);
    font-size: 13px;
  }

  &__no-videos-message {
    color: #666;
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 12px;
  }

  &__upload-section {
    margin-bottom: 12px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &--skeleton {
      height: 200px;
      align-content: flex-start;
      position: relative;
      overflow: hidden;
      mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__thumbnail {
    width: 113px;
    margin: 0 11px 11px 0;

    &--skeleton {
      @include loadingBgAnimation;
      height: 85px;
    }
  }

  &__sticky-panel {
    background: white;
    position: sticky;
    bottom: -40px;
  }

  &__selected-files-panel {
    margin-top: 8px;
  }
}

@media (max-width: $mobile-max-width) {
  .post-media-selection-panel {
    &__thumbnail {
      width: calc(100% / 3 - 12px);
    }

    &__sticky-panel {
      width: calc(100% + 40px);
      margin-left: -20px;
      bottom: -37px;
    }

    &__selected-files-panel {
      &::after {
        border-radius: 0;
      }
    }
  }
}
</style>
